import { useIntl } from 'react-intl';

import { Button, paletteColors } from '@evaneos/b2b-ui';
import PauseIcon from '@evaneos/b2b-ui/icons/Pause';
import PlayArrowIcon from '@evaneos/b2b-ui/icons/PlayArrow';
import RadioButtonCheckedIcon from '@evaneos/b2b-ui/icons/RadioButtonChecked';

import { CallStatus } from '../../../../../shared/providers/PhoneCallContext';

const PULSE_ANIMATION_STYLES = {
    '@keyframes pulse': {
        '0%': {
            boxShadow: `0 0 0 0 ${paletteColors.neutral[500]}D9`,
        },
        '70%': {
            boxShadow: `0 0 0 15px ${paletteColors.neutral[500]}00`,
        },
        '100%': {
            boxShadow: `0 0 0 0 ${paletteColors.neutral[500]}00`,
        },
    },
    animation: 'pulse 2s infinite',
    '&:hover': {
        animation: 'none',
    },
};

type RecordingButtonProps = {
    status: CallStatus;
    isRecording: boolean;
    isRecordingPaused: boolean;
    resumeRecording: () => Promise<void>;
    pauseRecording: () => Promise<void>;
    showRecordConfirmModal: boolean;
    shouldShowPulse: boolean;
    onButtonHover: () => void;
    handleConfirmRecording: () => void;
};

export const RecordingButton = ({
    status,
    isRecording,
    isRecordingPaused,
    resumeRecording,
    pauseRecording,
    showRecordConfirmModal,
    shouldShowPulse,
    onButtonHover,
    handleConfirmRecording,
}: RecordingButtonProps) => {
    const { formatMessage } = useIntl();

    if (!isRecording && !showRecordConfirmModal) {
        return (
            <Button
                onClick={handleConfirmRecording}
                onMouseEnter={onButtonHover}
                variant="outlined"
                size="medium"
                disabled={status !== 'active' || isRecording}
                sx={{
                    ...(shouldShowPulse && PULSE_ANIMATION_STYLES),
                }}
            >
                <RadioButtonCheckedIcon
                    fontSize="small"
                    sx={{ mr: 1, color: paletteColors.red[600] }}
                />
                {formatMessage({
                    id: 'experiment.better_business.phoning.start_recording',
                })}
            </Button>
        );
    }

    if (isRecording) {
        return (
            <Button
                onClick={() => void (isRecordingPaused ? resumeRecording() : pauseRecording())}
                data-testid="phoning-pause-resume-button"
                variant="outlined"
                size="medium"
                disabled={status !== 'active'}
                startIcon={
                    isRecordingPaused ? (
                        <PlayArrowIcon data-testid="phoning-resume-icon" fontSize="small" />
                    ) : (
                        <PauseIcon data-testid="phoning-pause-icon" fontSize="small" />
                    )
                }
            >
                {formatMessage({
                    id: isRecordingPaused ? 'phoning.resume' : 'phoning.pause',
                })}
            </Button>
        );
    }

    return null;
};
